/**
 * PlannerCases — Home page for EVENT_PLANNER_DESIGNER role.
 *
 * Shows all leads assigned to the current planner with:
 *   Customer · Occasion · Planning Scope · Planning Prepared % · Missing Info
 *   Latest Interaction · Next Planning Action
 *
 * Quick actions per card:
 *   Open Journey · Review Event Intent · Review Recommendations · Open Brief · Open Blueprint
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList, Calendar, Users, MapPin, AlertCircle,
  CheckCircle, Clock, ChevronRight, RefreshCw, Search,
  Sparkles, FileText, Lightbulb, BookOpen, Map, Zap,
  ArrowRight, Filter,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return null; }
}

function fmtRelative(iso) {
  if (!iso) return "No interactions yet";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return null; }
}

// ── Circular progress ring ────────────────────────────────────────────────────
function PreparedRing({ pct }) {
  const radius = 20;
  const circ = 2 * Math.PI * radius;
  const filled = (pct / 100) * circ;
  const color = pct < 30 ? "#B91C1C" : pct < 60 ? "#D97706" : pct < 85 ? "#C5A059" : "#16A34A";

  return (
    <div className="relative flex items-center justify-center" style={{ width: 52, height: 52 }}>
      <svg width="52" height="52" viewBox="0 0 52 52">
        <circle cx="26" cy="26" r={radius} fill="none" stroke="#F4F0EA" strokeWidth="4" />
        <circle
          cx="26" cy="26" r={radius} fill="none"
          stroke={color} strokeWidth="4"
          strokeDasharray={`${filled} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 26 26)"
          style={{ transition: "stroke-dasharray 0.5s ease" }}
        />
      </svg>
      <span
        className="absolute text-[11px] font-bold"
        style={{ color }}
      >
        {pct}%
      </span>
    </div>
  );
}

// ── Status chip ───────────────────────────────────────────────────────────────
const STATUS_STYLE = {
  NEW:        { bg: "#EFF6FF", color: "#1D4ED8", label: "New" },
  CONTACTED:  { bg: "#F0FDF4", color: "#15803D", label: "Contacted" },
  QUALIFYING: { bg: "#FFFBEB", color: "#92400E", label: "Qualifying" },
  QUALIFIED:  { bg: "#F0FDF4", color: "#15803D", label: "Qualified" },
  CONVERTED:  { bg: "#F5F3FF", color: "#6D28D9", label: "Converted" },
  LOST:       { bg: "#FEF2F2", color: "#B91C1C", label: "Lost" },
};
function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || { bg: "#F9F7F2", color: "#78716C", label: status };
  return (
    <span
      className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
      style={{ background: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
}

// ── Quick action button ───────────────────────────────────────────────────────
function QuickAction({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-150"
      style={{
        background: "#FAF7F2",
        border: "1px solid #E8E0D4",
        color: "#57534E",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = "#F7EFE4";
        e.currentTarget.style.borderColor = "var(--brand-gold, #C5A059)";
        e.currentTarget.style.color = "#1C1917";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = "#FAF7F2";
        e.currentTarget.style.borderColor = "#E8E0D4";
        e.currentTarget.style.color = "#57534E";
      }}
    >
      <Icon className="w-3 h-3 flex-shrink-0" />
      {label}
    </button>
  );
}

// ── Planning case card ────────────────────────────────────────────────────────
function CaseCard({ c, navigate }) {
  const hasJourney = !!c.linked_journey_id;

  const openLead = () => navigate(`/engage/leads/${c.lead_id}`);
  const openJourney = () => {
    if (c.linked_journey_id) navigate(`/journeys/${c.linked_journey_id}`);
    else openLead();
  };
  const reviewEventIntent = () => navigate(`/engage/leads/${c.lead_id}`);
  const reviewRecommendations = () => navigate(`/engage/leads/${c.lead_id}`);

  return (
    <div
      data-testid={`planner-case-card-${c.lead_id}`}
      className="bg-white rounded-xl p-5 transition-all duration-200 hover:shadow-md"
      style={{
        border: "1px solid #E8E0D4",
        boxShadow: "0 1px 4px rgba(28,25,23,0.04)",
      }}
    >
      {/* Top row: customer + status + ring */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3
              data-testid="case-customer-name"
              className="text-sm font-semibold truncate"
              style={{ color: "#1C1917" }}
            >
              {c.customer_name}
            </h3>
            <StatusChip status={c.status} />
          </div>
          <p className="text-xs truncate" style={{ color: "#78716C" }}>
            {c.lead_number}
            {c.customer_email && ` · ${c.customer_email}`}
          </p>
        </div>
        <PreparedRing pct={c.planning_prepared_pct} />
      </div>

      {/* Event intent row */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-3">
        {c.occasion && c.occasion !== "Not specified" && (
          <span className="flex items-center gap-1 text-xs" style={{ color: "#57534E" }}>
            <Sparkles className="w-3 h-3 flex-shrink-0" style={{ color: "#C5A059" }} />
            {c.occasion}
          </span>
        )}
        {c.planning_scope && (
          <span className="flex items-center gap-1 text-xs" style={{ color: "#57534E" }}>
            <ClipboardList className="w-3 h-3 flex-shrink-0" style={{ color: "#C5A059" }} />
            {c.planning_scope.replace(/_/g, " ")}
          </span>
        )}
        {c.event_date && (
          <span className="flex items-center gap-1 text-xs" style={{ color: "#57534E" }}>
            <Calendar className="w-3 h-3 flex-shrink-0" />
            {fmtDate(c.event_date)}
          </span>
        )}
        {c.guest_count && (
          <span className="flex items-center gap-1 text-xs" style={{ color: "#57534E" }}>
            <Users className="w-3 h-3 flex-shrink-0" />
            {c.guest_count} guests
          </span>
        )}
        {c.location && (
          <span className="flex items-center gap-1 text-xs" style={{ color: "#57534E" }}>
            <MapPin className="w-3 h-3 flex-shrink-0" />
            {c.location}
          </span>
        )}
      </div>

      {/* Missing information */}
      {c.missing_information && c.missing_information.length > 0 && (
        <div
          className="flex items-start gap-1.5 p-2 rounded-lg mb-3"
          style={{ background: "#FFF8F1", border: "1px solid #FDE8C8" }}
        >
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#D97706" }} />
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: "#92400E" }}>
              Missing Info
            </p>
            <p className="text-xs" style={{ color: "#78716C" }}>
              {c.missing_information.slice(0, 4).join(" · ")}
              {c.missing_information.length > 4 && ` +${c.missing_information.length - 4} more`}
            </p>
          </div>
        </div>
      )}

      {/* Next planning action */}
      <div
        className="flex items-center gap-2 p-2 rounded-lg mb-3"
        style={{ background: "#F9F7F2", border: "1px solid #EDE8DF" }}
      >
        <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#C5A059" }} />
        <p className="text-xs font-medium" style={{ color: "#1C1917" }}>
          {c.next_planning_action}
        </p>
      </div>

      {/* Footer: latest interaction + quick actions */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] flex items-center gap-1" style={{ color: "#A8A29E" }}>
          <Clock className="w-3 h-3" />
          {fmtRelative(c.latest_interaction)}
        </p>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {hasJourney && (
            <QuickAction
              icon={Map}
              label="Open Journey"
              onClick={openJourney}
              testId={`case-open-journey-${c.lead_id}`}
            />
          )}
          <QuickAction
            icon={Zap}
            label="Event Intent"
            onClick={reviewEventIntent}
            testId={`case-event-intent-${c.lead_id}`}
          />
          <QuickAction
            icon={Lightbulb}
            label="Recommendations"
            onClick={reviewRecommendations}
            testId={`case-recommendations-${c.lead_id}`}
          />
          <QuickAction
            icon={FileText}
            label="Brief"
            onClick={openLead}
            testId={`case-brief-${c.lead_id}`}
          />
        </div>
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ filtered }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
        style={{ background: "#F4F0EA" }}
      >
        <ClipboardList className="w-7 h-7" style={{ color: "#C5A059" }} />
      </div>
      <h3 className="text-base font-semibold mb-1" style={{ color: "#1C1917" }}>
        {filtered ? "No matching cases" : "No planning cases assigned yet"}
      </h3>
      <p className="text-sm max-w-xs" style={{ color: "#78716C" }}>
        {filtered
          ? "Try clearing your search filter."
          : "Leads will appear here once a CRM Manager assigns them to you."}
      </p>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function PlannerCases() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${API}/api/planner/cases`, { credentials: "include" });
      if (!resp.ok) throw new Error(`${resp.status}`);
      const data = await resp.json();
      setCases(data.cases || []);
    } catch (e) {
      setError("Could not load planning cases. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  // Filter
  const filtered = cases.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !search || (
      c.customer_name?.toLowerCase().includes(q) ||
      c.lead_number?.toLowerCase().includes(q) ||
      c.occasion?.toLowerCase().includes(q) ||
      c.customer_email?.toLowerCase().includes(q)
    );
    const matchStatus = filterStatus === "ALL" || c.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // Summary stats
  const totalCases = cases.length;
  const readyCases = cases.filter(c => c.planning_prepared_pct >= 80).length;
  const missingInfoCases = cases.filter(c => (c.missing_information || []).length > 0).length;
  const avgPct = cases.length
    ? Math.round(cases.reduce((a, c) => a + c.planning_prepared_pct, 0) / cases.length)
    : 0;

  return (
    <div className="min-h-screen" style={{ background: "var(--brand-ivory, #FAF7F2)" }}>
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* ── Header ── */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-1">
            <h1
              data-testid="planner-cases-heading"
              className="text-2xl font-bold"
              style={{ color: "#1C1917" }}
            >
              My Planning Cases
            </h1>
            <button
              data-testid="planner-cases-refresh"
              onClick={fetchCases}
              disabled={loading}
              className="p-2 rounded-lg transition-colors"
              style={{ color: "#78716C" }}
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
          <p className="text-sm" style={{ color: "#78716C" }}>
            Leads assigned to you — build planning briefs, review intent, and prepare for handoff.
          </p>
        </div>

        {/* ── Summary KPIs ── */}
        {!loading && !error && cases.length > 0 && (
        <div data-testid="planner-kpi-summary" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: "Total Cases", value: totalCases, icon: ClipboardList, color: "#C5A059" },
              { label: "Ready for Handoff", value: readyCases, icon: CheckCircle, color: "#16A34A" },
              { label: "Need More Info", value: missingInfoCases, icon: AlertCircle, color: "#D97706" },
              { label: "Avg Prepared", value: `${avgPct}%`, icon: Sparkles, color: "#7C3AED" },
            ].map(kpi => (
              <div
                key={kpi.label}
                className="bg-white rounded-xl p-4"
                style={{ border: "1px solid #E8E0D4" }}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium" style={{ color: "#78716C" }}>{kpi.label}</p>
                  <kpi.icon className="w-4 h-4" style={{ color: kpi.color }} />
                </div>
                <p className="text-2xl font-bold" style={{ color: "#1C1917" }}>{kpi.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── Filters ── */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#A8A29E" }} />
            <input
              data-testid="planner-cases-search"
              type="text"
              placeholder="Search by customer, occasion, lead number…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl bg-white outline-none transition-all"
              style={{
                border: "1px solid #E8E0D4",
                color: "#1C1917",
              }}
              onFocus={e => { e.target.style.borderColor = "#C5A059"; }}
              onBlur={e => { e.target.style.borderColor = "#E8E0D4"; }}
            />
          </div>
          <select
            data-testid="planner-cases-filter-status"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-2.5 text-sm rounded-xl bg-white outline-none"
            style={{ border: "1px solid #E8E0D4", color: "#1C1917" }}
          >
            <option value="ALL">All Statuses</option>
            <option value="NEW">New</option>
            <option value="CONTACTED">Contacted</option>
            <option value="QUALIFYING">Qualifying</option>
            <option value="QUALIFIED">Qualified</option>
            <option value="CONVERTED">Converted</option>
          </select>
        </div>

        {/* ── Content ── */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div
              className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: "#C5A059", borderTopColor: "transparent" }}
            />
          </div>
        )}

        {!loading && error && (
          <div
            data-testid="planner-cases-error"
            className="flex items-center gap-2 p-4 rounded-xl text-sm"
            style={{ background: "#FEF2F2", border: "1px solid rgba(185,28,28,0.2)", color: "#B91C1C" }}
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <EmptyState filtered={search || filterStatus !== "ALL"} />
        )}

        {!loading && !error && filtered.length > 0 && (
          <div data-testid="planner-cases-grid" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map(c => (
              <CaseCard key={c.lead_id} c={c} navigate={navigate} />
            ))}
          </div>
        )}

        {/* Result count */}
        {!loading && !error && filtered.length > 0 && (
          <p className="text-xs text-center mt-6" style={{ color: "#A8A29E" }}>
            Showing {filtered.length} of {cases.length} cases
          </p>
        )}
      </div>
    </div>
  );
}
